import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { InboxContact, InboxContacts, User } from 'src/constants/types';
import Contact from '../Contact';
import { NotePencil, MagnifyingGlass } from 'phosphor-react';
import { Dropdown, SectionHeading, TextInput } from 'src/UIKit';
import { VerticalScrollArea } from '../VerticalScrollArea';
import AwayMessageSwitcher from 'src/modules/campaigns/components/AwayMessage/AwayMessageSwitcher';

const FILTER_OPTIONS = [
  { value: 'all', title: 'All Active' },
  { value: 'unread', title: 'Unread Only' },
  { value: 'myconversations', title: 'Only My Conversations' },
  { value: 'spam', title: 'Spam' },
  { value: 'invalidcontacts', title: 'Invalid Contacts' },
  { value: 'optedoutcontacts', title: 'Opted-Out Contacts' },
];

type InboxLeftSectionProps = {
  isShowAll: boolean;
  onSelectContact: (contactId: string | null, contactPhoneNumber?: string) => void;
  onlyMyConversations: boolean;
  setOnlyMyConversations: React.Dispatch<React.SetStateAction<boolean>>;
  isSpam: boolean;
  setIsSpam: React.Dispatch<React.SetStateAction<boolean>>;
  isInvalidContacts: boolean;
  setIsInvalidContacts: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedContact: React.Dispatch<React.SetStateAction<InboxContact | undefined>>;
  applyFilter: (filter: string) => void;
  toggleShowAll: (value: boolean) => void;
  onSearchContact: (value: string) => void;
  contacts: InboxContacts | null;
  selectedContact: string | null;
  isContactsLoading: boolean;
  handleNewChatClick: () => void;
  fetchMoreData: () => void;
  totalContactsCount: number;
  hasMoreContacts: boolean;
  search: string;
  users: User[];
  setContactsOffset: React.Dispatch<React.SetStateAction<number>>;
};

function InboxLeftSection({
  onSelectContact,
  onSearchContact,
  applyFilter,
  selectedContact,
  contacts,
  isContactsLoading,
  handleNewChatClick,
  hasMoreContacts,
  fetchMoreData,
  search,
  users,
}: InboxLeftSectionProps) {
  const [searchParams] = useSearchParams();
  const [term, setTerm] = useState(search);
  const contactsListRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery<Theme>(t => t.breakpoints.down('md'));

  const getCurrentFilter = () => {
    return searchParams.get('filter') || 'all';
  };

  const [currentFilter, setCurrentFilter] = useState(getCurrentFilter());

  useEffect(() => {
    setTerm(search);
  }, [search]);

  // Debounced independently of onSearchContact's identity (recreated on every
  // parent re-render, e.g. every chat poll tick) — one timer tied to `term`,
  // always cleaned up on the next keystroke or unmount.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      onSearchContact(term);
    }, 500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [term, onSearchContact]);

  useEffect(() => {
    const urlFilter = getCurrentFilter();
    setCurrentFilter(urlFilter);
  }, [searchParams]);

  useEffect(() => {
    const contactsList = contactsListRef.current;
    if (contactsList) {
      contactsList.addEventListener('scroll', onScroll);

      // Clean-up
      return () => {
        contactsList.removeEventListener('scroll', onScroll);
      };
    }
  }, [contactsListRef, contacts?.items, hasMoreContacts]);

  const handleSelectChange = (filter: string) => {
    setCurrentFilter(filter);
    applyFilter(filter);
  };

  const onScroll = () => {
    if (contactsListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contactsListRef.current;
      const isNearBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 2;
      if (isNearBottom) {
        if (hasMoreContacts) {
          fetchMoreData();
        }
      }
    }
  };

  return (
    <Box
      position="relative"
      minHeight="100%"
      height="100%"
      width={isMobile ? '100%' : 263}
      overflow="hidden"
      bgcolor="background.paper"
      boxShadow={isMobile ? 'none' : t => t.customShadows.card}
    >
      <Stack height={isMobile ? '90dvh' : '85vh'} maxHeight={isMobile ? '90dvh' : '85vh'}>
        <Stack spacing={2}>
          <Stack
            p={2}
            pt={isMobile ? 0 : 2}
            pb={0}
            alignItems="center"
            justifyContent="space-between"
            direction="row"
          >
            <SectionHeading>Inbox</SectionHeading>
            <IconButton aria-label="New chat" onClick={handleNewChatClick}>
              <NotePencil />
            </IconButton>
          </Stack>
          <Box px={2}>
            <AwayMessageSwitcher />
          </Box>
          <Stack p={2} pt={0} spacing={2}>
            <Dropdown
              value={currentFilter}
              options={FILTER_OPTIONS}
              onChange={(_, filter) => handleSelectChange(filter)}
              size="small"
              margin="none"
            />
            <TextInput
              value={term}
              onChange={(_, value) => {
                setTerm(value);
                onSelectContact(null);
              }}
              placeholder="Search Contacts..."
              inputProps={{ 'aria-label': 'Search contacts' }}
              margin="none"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MagnifyingGlass />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Stack>
        {isContactsLoading && (
          <Stack flexGrow={1} alignItems="center" justifyContent="center">
            <CircularProgress size={24} color="secondary" />
          </Stack>
        )}
        {!isContactsLoading && contacts?.items?.length === 0 && (
          <Stack flexGrow={1} alignItems="center" justifyContent="center">
            <Typography color="text.secondary">No Results</Typography>
          </Stack>
        )}
        {!isContactsLoading && Boolean(contacts?.items?.length) && (
          <VerticalScrollArea ref={contactsListRef} flexGrow={1} minHeight={0}>
            {contacts?.items?.map(el => (
              <Fragment key={el.contact.id}>
                <Contact
                  users={users}
                  contact={el}
                  selectedContact={selectedContact}
                  onSelectChat={onSelectContact}
                />
                <Divider />
              </Fragment>
            ))}
          </VerticalScrollArea>
        )}
      </Stack>
    </Box>
  );
}

export default InboxLeftSection;
